// src/components/DocumentViewer.jsx
"use client";
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Stack, Typography, Button, Tooltip, Box, LinearProgress
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DownloadIcon from "@mui/icons-material/Download";

import { Document, Page, pdfjs } from "react-pdf";
// pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//   "pdf.worker.min.js",
//   import.meta.url.replace(/\/[^/]*$/, "/") + "pdf.worker.min.js"
// ).toString();

pdfjs.GlobalWorkerOptions.workerSrc =
   `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
const isPdf = (ct = "", url = "") =>
  /application\/pdf/i.test(ct) || /\.pdf(\?|$)/i.test(url);
const isImage = (ct = "", url = "") =>
  /^image\//i.test(ct) || /\.(png|jpe?g|gif|webp|avif|bmp|svg)(\?|$)/i.test(url);

export default function DocumentViewer({
  open,
  onClose,
  fileUrl,
  fileName,
  contentType
}) {
  const [scale, setScale] = useState(1.0);
  const [rotate, setRotate] = useState(0);
  const [numPages, setNumPages] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setScale(1);
      setRotate(0);
      setPage(1);
    }
  }, [open, fileUrl]);

  const canPdf = isPdf(contentType, fileUrl);
  const canImg = isImage(contentType, fileUrl);

  const zoomIn  = () => setScale((s) => Math.min(4, +(s + 0.2).toFixed(2)));
  const zoomOut = () => setScale((s) => Math.max(0.4, +(s - 0.2).toFixed(2)));
  const reset   = () => { setScale(1); setRotate(0); };
  const next    = () => setPage((p) => Math.min((numPages || 1), p + 1));
  const prev    = () => setPage((p) => Math.max(1, p - 1));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" PaperProps={{ sx: { height: "90vh" } }}>
      <DialogTitle sx={{ pr: 1 }}>
        {fileName || "Document"}
        <Stack direction="row" spacing={0.5} sx={{ float: "right" }}>
          <Tooltip title="Open in new tab">
            <IconButton onClick={() => window.open(fileUrl, "_blank", "noopener")}>
              <OpenInNewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download">
            <IconButton component="a" href={fileUrl} download={fileName || true}>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ display: "flex", flexDirection: "column", p: 0 }}>
        {/* Toolbar */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.25, borderBottom: "1px solid", borderColor: "divider" }}>
          <Tooltip title="Zoom out"><span><IconButton onClick={zoomOut} disabled={scale <= 0.4}><ZoomOutIcon/></IconButton></span></Tooltip>
          <Typography variant="body2" sx={{ minWidth: 64, textAlign: "center" }}>{Math.round(scale * 100)}%</Typography>
          <Tooltip title="Zoom in"><span><IconButton onClick={zoomIn} disabled={scale >= 4}><ZoomInIcon/></IconButton></span></Tooltip>
          <Tooltip title="Rotate 90°"><IconButton onClick={() => setRotate((r) => (r + 90) % 360)}><RotateRightIcon/></IconButton></Tooltip>
          <Tooltip title="Reset"><IconButton onClick={reset}><RestartAltIcon/></IconButton></Tooltip>
          <Box sx={{ flexGrow: 1 }} />
          {canPdf && (
            <Stack direction="row" spacing={1} alignItems="center">
              <IconButton onClick={prev} disabled={page <= 1}><ArrowBackIcon/></IconButton>
              <Typography variant="body2">Page {page}{numPages ? ` / ${numPages}` : ""}</Typography>
              <IconButton onClick={next} disabled={numPages ? page >= numPages : true}><ArrowForwardIcon/></IconButton>
            </Stack>
          )}
        </Stack>

        {/* Canvas / Content */}
        <Box sx={{ flex: 1, overflow: "auto", display: "grid", placeItems: "center", bgcolor: "background.default", p: 2 }}>
          {canPdf ? (
            <Box sx={{ transform: `scale(${scale}) rotate(${rotate}deg)`, transformOrigin: "top center" }}>
              <Document
                file={fileUrl}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                loading={
                  <Box sx={{ width: 360, p: 2 }}>
                    <LinearProgress />
                    <Typography variant="caption">Loading PDF…</Typography>
                  </Box>
                }
                onLoadError={() => {}}
              >
                <Page pageNumber={page} renderTextLayer={false} renderAnnotationLayer={false} />
              </Document>
            </Box>
          ) : canImg ? (
            <img
              src={fileUrl}
              alt={fileName || "image"}
              style={{
                transform: `scale(${scale}) rotate(${rotate}deg)`,
                transformOrigin: "center center",
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                display: "block"
              }}
            />
          ) : (
            // Fallback: try iframe. Some types (docx/xlsx) won't render natively.
            <iframe
              src={fileUrl}
              title={fileName || "preview"}
              style={{ width: "100%", height: "100%", border: 0 }}
              onLoad={() => setLoading(false)}
              onError={() => setLoading(false)}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
